import React, { useState, useEffect } from "react";
import { Sidebar } from "primereact/sidebar";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import ReactMarkdown from "react-markdown";

interface PhaseGuide {
  phase1: {
    title: string;
    content: string;
  };
  phase2: {
    title: string;
    content: string;
  };
  phase3: {
    title: string;
    content: string;
  };
}

interface GuideData {
  [templateId: string]: PhaseGuide;
}

interface GuidePanelProps {
  visible: boolean;
  templateId: string | null;
  currentView: any;
  onClose: () => void;
  onReselect: () => void;
  onOpen: () => void;
}

const GuidePanel: React.FC<GuidePanelProps> = ({
  visible,
  templateId,
  currentView,
  onClose,
  onReselect,
  onOpen,
}) => {
  const [guideData, setGuideData] = useState<PhaseGuide | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Map currentView to accordion index
  useEffect(() => {
    if (
      currentView?.label === "Visualize" ||
      currentView?.label === "Canvasize"
    ) {
      setActiveIndex(0);
    } else if (currentView?.label === "Organize") {
      setActiveIndex(1);
    } else if (currentView?.label === "Realize") {
      setActiveIndex(2);
    } else {
      setActiveIndex(0); // Default to first accordion
    }
  }, [currentView]);

  useEffect(() => {
    const loadGuideData = async () => {
      // Default fallback guidance
      const defaultGuideData: PhaseGuide = {
        phase1: {
          title: "1. Visualize",
          content:
            '**Purpose:** Understand the big picture, user needs, and define the "why."\n\n- Define your vision and goals\n- Identify stakeholders\n- Establish success criteria',
        },
        phase2: {
          title: "2. Organize",
          content:
            "**Purpose:** Structure work, prioritize, and prepare for execution.\n\n- Break down into actionable items\n- Prioritize and organize\n- Plan your approach",
        },
        phase3: {
          title: "3. Realize",
          content:
            "**Purpose:** Deliver value, iterate, reflect, and improve.\n\n- Execute your plan\n- Track progress\n- Reflect and adapt",
        },
      };

      if (!templateId) {
        // No template selected (e.g., user skipped and started blank)
        setGuideData(defaultGuideData);
        setLoading(false);
        return;
      }

      try {
        const response = await fetch("/Data/phase-guidance.json");
        const data: GuideData = await response.json();

        if (data[templateId]) {
          setGuideData(data[templateId]);
        } else {
          // Template not found in guidance data, use default
          setGuideData(defaultGuideData);
        }
      } catch (error) {
        console.error("Error loading guide data:", error);
        // On error, show default guidance instead of nothing
        setGuideData(defaultGuideData);
      } finally {
        setLoading(false);
      }
    };

    loadGuideData();
  }, [templateId]);

  return (
    <div className="card flex justify-content-center">
      <Sidebar
        showCloseIcon={true}
        className="guide-panel"
        style={{
          paddingRight: "30px",
          display: "flex",
          flexDirection: "column",
        }}
        visible={visible}
        onHide={onClose}
        dismissable={false}
        modal={false}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "1rem",
            marginTop: "0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Tag
              value="💡"
              style={{
                backgroundColor: "var(--focusmap-surface-2)",
                color: "var(--focusmap-primary)",
                fontSize: "20px",
                padding: "8px 12px",
              }}
            />
            <h2 style={{ margin: 0, color: "var(--focusmap-title)" }}>
              Guide Me
            </h2>
          </div>
        </div>

        {loading ? (
          <div style={{ color: "var(--focusmap-text)", padding: "20px" }}>
            <p>Loading guidance...</p>
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: "auto" }}>
            <Accordion
              activeIndex={activeIndex}
              onTabChange={(e) => setActiveIndex(e.index as number)}
            >
              <AccordionTab header={guideData.phase1.title}>
                <div className="phase-content">
                  <ReactMarkdown>{guideData.phase1.content}</ReactMarkdown>
                </div>
              </AccordionTab>
              <AccordionTab header={guideData.phase2.title}>
                <div className="phase-content">
                  <ReactMarkdown>{guideData.phase2.content}</ReactMarkdown>
                </div>
              </AccordionTab>
              <AccordionTab header={guideData.phase3.title}>
                <div className="phase-content">
                  <ReactMarkdown>{guideData.phase3.content}</ReactMarkdown>
                </div>
              </AccordionTab>
            </Accordion>
            <div
              style={{
                marginTop: "20px",
                paddingTop: "20px",
                borderTop: "1px solid var(--focusmap-border)",
                paddingRight: "30px",
              }}
            >
              <Button
                label="Choose Different Template"
                icon="pi pi-refresh"
                onClick={onReselect}
                className="p-button-outlined"
                style={{
                  width: "calc(100% - 30px)",
                  color: "var(--focusmap-text)",
                  borderColor: "var(--focusmap-border)",
                }}
              />
            </div>
          </div>
        )}
      </Sidebar>
      <Tag
        className={`left-sidebar-toggle-button${
          visible ? " left-sidebar-open" : ""
        }`}
        value=""
        onClick={() => {
          if (visible) {
            onClose();
          } else {
            onOpen();
          }
        }}
        rounded
        style={{ cursor: "pointer" }}
      >
        <i
          className="pi pi-compass"
          style={{ fontSize: "1.8em", marginLeft: "0.6em", color: "#fff" }}
        ></i>
      </Tag>
    </div>
  );
};

export default GuidePanel;
